import React from 'react';
import { Link } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { motion } from 'framer-motion';
import {
  Users,
  Package,
  Award,
  Calendar,
  Activity,
  ArrowUpRight,
  Image as ImageIcon,
  MessageSquare,
  Box,
  Clock
} from 'lucide-react';

import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area
} from 'recharts';

const Dashboard = () => {
  const { data, loading } = useData();

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center bg-gray-50/50 rounded-3xl">
        <div className="w-10 h-10 border-4 border-sugarcane-green border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const productsCount = data.products?.length || 0;
  const awardsCount = (data.awards?.national?.length || 0) + (data.awards?.state?.length || 0) + (data.awards?.safety?.length || 0);
  const newsCount = (data.news?.latest?.length || 0) + (data.news?.upcoming?.length || 0) + (data.news?.press?.length || 0);
  const boardCount = (data.about?.directors?.length || 0) + (data.about?.chairman?.name ? 1 : 0);
  const galleryImagesCount = data.gallery?.images?.length || 0;

  // Inquiries breakdown
  const allInquiries = data.inquiries || [];
  const isProductInquiry = (i) => Boolean(i.productName || i.quantityRequired);

  const contactInquiries = allInquiries.filter(i => !isProductInquiry(i));
  const productInquiries = allInquiries.filter(i => isProductInquiry(i));

  const contactPending = contactInquiries.filter(i => i.status === 'pending').length;
  const productPending = productInquiries.filter(i => i.status === 'pending').length;

  const stats = [
    { label: 'Total Products', value: productsCount, icon: Package, path: '/admin/products', color: 'text-blue-600 bg-blue-50 border-blue-100/50', hoverColor: 'group-hover:bg-blue-600 group-hover:text-white', trend: 'Active catalog' },
    { label: 'Total Awards', value: awardsCount, icon: Award, path: '/admin/awards', color: 'text-sugarcane-green bg-green-50 border-green-100/50', hoverColor: 'group-hover:bg-sugarcane-green group-hover:text-white', trend: 'Achievements' },
    { label: 'News Updates', value: newsCount, icon: Calendar, path: '/admin/news', color: 'text-purple-600 bg-purple-50 border-purple-100/50', hoverColor: 'group-hover:bg-purple-600 group-hover:text-white', trend: 'Announcements' },
    { label: 'Board Members', value: boardCount, icon: Users, path: '/admin/about', color: 'text-orange-600 bg-orange-50 border-orange-100/50', hoverColor: 'group-hover:bg-orange-600 group-hover:text-white', trend: 'Executive board' },
    { label: 'Gallery Photos', value: galleryImagesCount, icon: ImageIcon, path: '/admin/gallery', color: 'text-teal-600 bg-teal-50 border-teal-100/50', hoverColor: 'group-hover:bg-teal-600 group-hover:text-white', trend: 'Media catalog' },
  ];

  // Dynamic calendar 6-month calculation
  const getDynamicMonthlyChartData = () => {
    const result = [];
    const now = new Date();

    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthLabel = d.toLocaleString('en-US', { month: 'short' });
      const year = d.getFullYear();
      const monthIndex = d.getMonth();

      const startOfMonth = new Date(year, monthIndex, 1, 0, 0, 0);
      const endOfMonth = new Date(year, monthIndex + 1, 0, 23, 59, 59);

      const itemsInMonth = allInquiries.filter(item => {
        if (!item.timestamp) return false;
        const itemDate = item.timestamp.seconds 
          ? new Date(item.timestamp.seconds * 1000)
          : new Date(item.timestamp);
        return itemDate >= startOfMonth && itemDate <= endOfMonth;
      });

      const contactCount = itemsInMonth.filter(i => !isProductInquiry(i)).length;
      const productCount = itemsInMonth.filter(i => isProductInquiry(i)).length;

      result.push({
        name: monthLabel, // e.g. 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'
        totalInquiries: itemsInMonth.length,
        contactInquiries: contactCount,
        productInquiries: productCount
      });
    }

    return result;
  };

  const chartData = getDynamicMonthlyChartData();

  return (
    <div className="space-y-10 font-poppins">
      {/* Welcome Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-3xl font-black text-sugarcane-charcoal mb-2">System Overview</h1>
          <p className="text-gray-500 font-medium italic">Welcome back, Admin. Here's what's happening today.</p>
        </div>
        <div className="flex items-center space-x-3 bg-white p-2 rounded-2xl border border-gray-100 shadow-sm">
          <div className="bg-sugarcane-green/10 text-sugarcane-green p-2 rounded-xl">
            <Activity size={20} />
          </div>
          <span className="text-sm font-bold text-sugarcane-charcoal pr-4">System Live</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {stats.map((stat, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.05 }}
          >
            <Link
              to={stat.path}
              className="relative bg-white p-6 rounded-3xl border border-gray-100/80 shadow-sm hover:shadow-xl hover:border-sugarcane-green/35 transition-all duration-500 flex flex-col justify-between group overflow-hidden cursor-pointer min-h-[160px]"
            >
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-gray-50 to-gray-100 rounded-full translate-x-8 -translate-y-8 group-hover:scale-150 transition-transform duration-700 ease-out z-0 opacity-40"></div>
              
              <div className="relative z-10 space-y-4">
                <div className="flex items-center justify-between">
                  <div className={`w-11 h-11 rounded-2xl flex items-center justify-center border ${stat.color} ${stat.hoverColor} transition-all duration-300 shadow-sm group-hover:scale-110`}>
                    <stat.icon size={20} />
                  </div>
                  <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">{stat.trend}</span>
                </div>
                
                <div>
                  <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest mb-1">{stat.label}</p>
                  <h3 className="text-3xl font-black text-sugarcane-charcoal tracking-tight group-hover:text-sugarcane-green transition-colors">{stat.value}</h3>
                </div>
              </div>
              
              <div className="absolute bottom-6 right-6 text-gray-300 group-hover:text-sugarcane-green group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300">
                <ArrowUpRight size={18} />
              </div>
            </Link>
          </motion.div>
        ))}

        {/* COMBINED INQUIRIES STAT CARD (Contact Us + Product Inquiries split with line) */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
        >
          <Link
            to="/admin/inquiries"
            className="relative bg-white p-6 rounded-3xl border border-rose-100/80 shadow-sm hover:shadow-xl hover:border-sugarcane-green/35 transition-all duration-500 flex flex-col justify-between group overflow-hidden cursor-pointer min-h-[160px]"
          >
            <div className="flex justify-between items-center mb-3">
              <span className="text-[10px] font-black uppercase tracking-widest text-rose-500 bg-rose-50 px-2.5 py-1 rounded-md border border-rose-100">
                 Customer Inquiries
              </span>
              <ArrowUpRight size={18} className="text-gray-300 group-hover:text-sugarcane-green transition-all" />
            </div>

            {/* Split Content with vertical divider line */}
            <div className="grid grid-cols-2 gap-4 items-center pt-2">
              {/* Left: Contact Us Inquiries */}
              <div className="pr-4 border-r border-gray-200">
                <div className="flex items-center space-x-1.5 mb-1 text-gray-400">
                  <MessageSquare size={14} className="text-rose-500" />
                  <span className="text-[10px] font-extrabold uppercase tracking-wider">Contact Us</span>
                </div>
                <div className="flex items-baseline space-x-2">
                  <h4 className="text-2xl font-black text-sugarcane-charcoal">{contactInquiries.length}</h4>
                  {contactPending > 0 && (
                    <span className="text-[10px] font-bold text-amber-600 flex items-center">
                      <Clock size={10} className="mr-0.5" />{contactPending} pending
                    </span>
                  )}
                </div>
              </div>

              {/* Right: Product Inquiries */}
              <div className="pl-2">
                <div className="flex items-center space-x-1.5 mb-1 text-gray-400">
                  <Box size={14} className="text-sugarcane-green" />
                  <span className="text-[10px] font-extrabold uppercase tracking-wider">Product Page</span>
                </div>
                <div className="flex items-baseline space-x-2">
                  <h4 className="text-2xl font-black text-sugarcane-charcoal">{productInquiries.length}</h4>
                  {productPending > 0 && (
                    <span className="text-[10px] font-bold text-amber-600 flex items-center">
                      <Clock size={10} className="mr-0.5" />{productPending} pending
                    </span>
                  )}
                </div>
              </div>
            </div>
          </Link>
        </motion.div>
      </div>

      {/* FULL WIDTH DYNAMIC MONTHLY INQUIRIES CHART */}
      <div className="bg-white p-6 md:p-8 rounded-3xl border border-gray-100 shadow-sm overflow-hidden relative hover:shadow-md transition-all duration-300">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
          <div>
            <h3 className="text-2xl font-black text-sugarcane-charcoal">Monthly Inquiries</h3>
            <p className="text-xs text-gray-400 font-medium italic mt-1">
              Combined real-time tracking of product & contact us inquiries over dynamic 6-month rolling calendar.
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="flex items-center text-xs font-bold text-gray-600">
              <div className="w-3 h-3 bg-sugarcane-green rounded-full mr-2"></div>
              Total Combined Inquiries
            </div>
          </div>
        </div>

        <div className="h-[360px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="colorInquiries" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2E7D32" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#2E7D32" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280', fontWeight: 'bold' }} dy={10} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6b7280', fontWeight: 'bold' }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  borderRadius: '16px',
                  border: '1px solid #f3f4f6',
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                  padding: '12px 16px'
                }}
              />
              <Area type="monotone" dataKey="totalInquiries" name="Total Inquiries" stroke="#2E7D32" strokeWidth={4} fillOpacity={1} fill="url(#colorInquiries)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
